import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, Camera, MessageSquare, Wrench, Star, ShieldCheck,
  Sparkles, Search, FileText, CheckCircle2,
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useSmartCTA } from "@/hooks/useSmartCTA";

const CLIENT_ICONS = [Camera, FileText, MessageSquare, CheckCircle2];
const PROVIDER_ICONS = [Sparkles, Search, FileText, Wrench];

export default function HowItWorks() {
  const cta = useSmartCTA();
  const { t } = useTranslation();

  const clientSteps = [1, 2, 3, 4].map((n, i) => ({
    Icon: CLIENT_ICONS[i],
    title: t(`howItWorks.clients.s${n}Title`),
    body: t(`howItWorks.clients.s${n}Body`),
  }));
  const providerSteps = [1, 2, 3, 4].map((n, i) => ({
    Icon: PROVIDER_ICONS[i],
    title: t(`howItWorks.fixybees.s${n}Title`),
    body: t(`howItWorks.fixybees.s${n}Body`),
  }));

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="how-it-works-page">
      <Navbar />
      <section className="section bg-honeycomb pb-8" data-testid="how-hero">
        <div className="container-app text-center">
          <h1 className="font-display text-5xl md:text-6xl font-extrabold leading-tight text-worker">
            {t("howItWorks.headline")}
          </h1>
          <p className="mt-4 text-lg text-smoke max-w-2xl mx-auto">{t("howItWorks.subhead")}</p>
        </div>
      </section>

      {/* Clients */}
      <section className="section" data-testid="clients-flow">
        <div className="container-app">
          <span className="badge mb-3"><Star size={12} className="text-hive-ink" /> {t("howItWorks.clients.kicker")}</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-worker">{t("howItWorks.clients.title")}</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {clientSteps.map((s, i) => {
              const Icon = s.Icon;
              return (
                <div key={i} className="card-base p-5">
                  <Icon size={26} className="text-hive-ink" />
                  <h3 className="mt-3 font-display text-lg font-bold text-worker">{s.title}</h3>
                  <p className="mt-2 text-sm text-smoke leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to={cta.postJob()} className="fx-btn fx-btn--primary" data-testid="client-cta">
              {cta.isLoggedIn ? t("howItWorks.clients.ctaAuth") : t("howItWorks.clients.cta")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Providers */}
      <section className="section bg-worker text-cream" data-testid="providers-flow">
        <div className="container-app">
          <span className="badge bg-hive text-worker mb-3">{t("howItWorks.fixybees.kicker")}</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold">{t("howItWorks.fixybees.title")}</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {providerSteps.map((s, i) => {
              const Icon = s.Icon;
              return (
                <div key={i} className="rounded-md bg-espresso/60 border border-cream/10 p-5">
                  <Icon size={26} className="text-hive" />
                  <h3 className="mt-3 font-display text-lg font-bold">{s.title}</h3>
                  <p className="mt-2 text-sm text-cream/80 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            {/* "Become a FixyBee — free" is a signup pitch. To a FixyBee it is
                also a link to their own dashboard, which made the label the
                only part still addressed to a stranger. */}
            <Link to={cta.becomeFixyBee()} className="fx-btn fx-btn--master" data-testid="provider-cta">
              {cta.isProvider ? t("howItWorks.fixybees.ctaAuth") : t("howItWorks.fixybees.cta")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
